import { useEffect, useState, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCreditStats } from '../store/reducers/index';
import apiFetch from '../utils/api';
import {
  fetchAgentsFromApi,
  fetchSendersFromApi,
  fetchReceiversFromApi,
  refreshAgents,
  refreshSenders,
  refreshReceivers,
  refreshSendersAndReceivers,
} from '../utils/contactsApi';

export { refreshAgents, refreshSenders, refreshReceivers, refreshSendersAndReceivers };

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

export const useUserLoaded = () => {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) {
          // Silently skip if no token (user not logged in yet)
          return;
        }
        
        // Always fetch user data to ensure we have the latest information
        const response = await apiFetch(`${API_BASE_URL}/api/users/me`, {
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json'
          }
        });
  
        if (!response.ok) {
          throw new Error('Failed to fetch user details');
        }
  
        const data = await response.json();
        
        // Store user data in session storage
        sessionStorage.setItem('user_id', data.id);

        dispatch({ type: 'UPDATE_USER', payload: data });
        
        setIsLoaded(true);
        
      } catch (err) {
        console.error("Error fetching user:", err.message);
        
        if (sessionStorage.getItem('user_id')) {
          setIsLoaded(true);
        }
      }
    };
  
    fetchUser();
  }, []);
  
  return isLoaded;
};

export const useCompany = () => {
  const dispatch = useDispatch();
  const isUserLoaded = useUserLoaded();

  useEffect(() => {
    if (!isUserLoaded) return;
    
    const fetchCompany = async () => {
      const userId = sessionStorage.getItem('user_id');
      
      if (!userId) {
        return;
      }
      
      try {
        const response = await apiFetch(
          `${API_BASE_URL}/api/items/company`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'accept': 'application/json'
            }
          }
        );
  
        if (!response.ok) {
          throw new Error('Failed to fetch company');
        }
  
        const data = await response.json();
        
        // Get the first item if it's an array, or use the data directly
        const companyData = data.data ? 
          (Array.isArray(data.data) ? data.data[0] : data.data) : 
          (Array.isArray(data) ? data[0] : data);
        
        dispatch({ type: 'SET_COMPANY', payload: companyData });
      } catch (err) {
        console.error("Error fetching company:", err.message);
        dispatch({ type: 'SET_NEEDS_COMPANY_CREATION', payload: true });
      }
    };
  
    fetchCompany();
  }, [dispatch, isUserLoaded]);
};

export const useAgents = () => {
  const dispatch = useDispatch();
  const isUserLoaded = useUserLoaded();

  useEffect(() => {
    if (!isUserLoaded) return;
    
    const fetchAgents = async () => {
      try {
        const agentsData = await fetchAgentsFromApi();
        dispatch({ type: 'SET_AGENTS', payload: agentsData });
      } catch (err) {
        if (err.message !== 'No user_id found in sessionStorage') {
          console.error('Error fetching agents:', err.message);
        }
      }
    };
  
    fetchAgents();
  }, [dispatch, isUserLoaded]);
};

export const useSenders = () => {
  const dispatch = useDispatch();
  const isUserLoaded = useUserLoaded();

  useEffect(() => {
    if (!isUserLoaded) return;

    const fetchSenders = async () => {
      try {
        const sendersData = await fetchSendersFromApi();
        dispatch({ type: 'SET_SENDERS', payload: sendersData });
      } catch (err) {
        if (err.message !== 'No user_id found in sessionStorage') {
          console.error('Error fetching senders:', err.message);
        }
      }
    };

    fetchSenders();
  }, [dispatch, isUserLoaded]);
};

export const useReceivers = () => {
  const dispatch = useDispatch();
  const isUserLoaded = useUserLoaded();

  useEffect(() => {
    if (!isUserLoaded) return;

    const fetchReceivers = async () => {
      try {
        const receiversData = await fetchReceiversFromApi();
        dispatch({ type: 'SET_RECEIVERS', payload: receiversData });
      } catch (err) {
        if (err.message !== 'No user_id found in sessionStorage') {
          console.error('Error fetching receivers:', err.message);
        }
      }
    };

    fetchReceivers();
  }, [dispatch, isUserLoaded]);
};

export const useCreditStats = () => {
  const dispatch = useDispatch();
  const { current, usage, exportCost, loading, error } = useSelector(state => state.creditStats);

  const refetch = () => {
    dispatch(fetchCreditStats());
  };

  useEffect(() => {
    dispatch(fetchCreditStats());
  }, [dispatch]);

  return { current, usage, exportCost, loading, error, refetch };
};

export function useContactRefresh(refreshFn) {
  const dispatch = useDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshFnRef = useRef(refreshFn);
  refreshFnRef.current = refreshFn;

  const refresh = useCallback(async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (event?.currentTarget) {
      event.currentTarget.blur();
    }

    flushSync(() => setIsRefreshing(true));
    try {
      return await refreshFnRef.current(dispatch);
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch]);

  return { refresh, isRefreshing };
}
